import { memo, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { isPremiumTemplate } from '@/constants/templates';
import { useInvite } from '@/hooks/useInvite';
import { InviteCardSkeleton } from '@/components/LoadingSkeleton';
import { getThemeStyles } from '@/utils/theme';

// Layouts
import StandardInviteLayout from '@/layouts/standard/StandardInviteLayout';
import CharmingNaturalLayout from '@/layouts/premium/charming-natural/CharmingNaturalLayout';

import styles from './InvitePage.module.css';

/**
 * Страница приглашения
 *
 * Ответственность:
 * - Загрузка данных приглашения
 * - Определение типа темы (стандарт/премиум)
 * - Рендеринг соответствующего layout
 * - Обработка состояний загрузки/ошибок
 */
const InvitePage = memo(() => {
  const { id } = useParams<{ id: string }>();
  const { invite, loading, error } = useInvite(id);

  const defaultThemeStyles = useMemo(() => getThemeStyles(), []);

  // Определяем, какой layout использовать
  const template = invite?.template || 'classic';
  const isPremium = isPremiumTemplate(template);

  // Устанавливаем data-theme для CSS fallback
  useMemo(() => {
    if (typeof window !== 'undefined' && invite?.template) {
      const root = document.getElementById('root');
      const { body } = document;
      if (root) {
        root.setAttribute('data-theme', template);
      }
      if (body) {
        body.setAttribute('data-theme', template);
      }
    }
  }, [template, invite?.template]);

  if (loading) {
    return (
      <div className={`${defaultThemeStyles.container} ${styles.container}`}>
        <div className={styles.skeletonContainer}>
          <InviteCardSkeleton />
        </div>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className={`${defaultThemeStyles.container} ${styles.container}`}>
        <div className={`${defaultThemeStyles.error || ''} ${styles.error}`}>
          <h2 className={`${defaultThemeStyles.errorTitle || ''} ${styles.errorTitle}`}>
            Ошибка
          </h2>
          <p>{error || 'Приглашение не найдено'}</p>
        </div>
      </div>
    );
  }

  // Выбираем layout на основе типа темы
  if (isPremium) {
    // Премиум layouts обрабатываются через switch для масштабируемости
    switch (template) {
      case 'charming-natural':
        return <CharmingNaturalLayout invite={invite} />;
      default:
        // Fallback на стандартный layout, если премиум тема не найдена
        return <StandardInviteLayout invite={invite} />;
    }
  }

  // Стандартные темы используют один layout с разными стилями
  return <StandardInviteLayout invite={invite} />;
});

InvitePage.displayName = 'InvitePage';

export default InvitePage;
